/**
 * Connection Card Component
 *
 * Card showing connection overview with:
 * - Platform logo and name
 * - Status badge (computed display state)
 * - Health indicator
 * - Account name (external company ID)
 * - Last health check
 * - Actions menu
 * - Expandable technical details
 *
 * Persona-focused design:
 * - Primary info for Debra (simple, visual)
 * - Technical details for Sarah (expandable)
 */

import { Card, Space, Typography, Avatar } from 'antd'
import type { Connection, ConnectionAction } from '@/types/connection.types'
import { ConnectionStatusBadge } from './ConnectionStatusBadge'
import { ConnectionHealthIcon } from './ConnectionHealthIcon'
import { ConnectionTechnicalDetails } from './ConnectionTechnicalDetails'
import { ConnectionActionsMenu } from './ConnectionActionsMenu'
import { getPlatformDisplayName } from '@/utils/connectionState'

const { Text, Title } = Typography

/**
 * Props for ConnectionCard component
 */
export interface ConnectionCardProps {
  /** Connection entity */
  connection: Connection

  /** Callback when action is selected */
  onAction: (action: ConnectionAction, connection: Connection) => void

  /** Loading state (disables actions) */
  loading?: boolean

  /** Custom style */
  style?: React.CSSProperties

  /** Custom class name */
  className?: string
}

/**
 * Get platform logo/icon (placeholder until we have real logos)
 * TODO: Replace with actual platform logos
 */
function getPlatformIcon(platform: string): React.ReactNode {
  // For now, use colored avatars with first letter
  const platformColors: Record<string, string> = {
    square: '#3E4348',
    'square-sandbox': '#7B8894',
    quickbooks: '#2CA01C',
    'quickbooks-sandbox': '#8BC34A',
    shopify: '#96BF48',
    'shopify-sandbox': '#C5E1A5',
  }

  const basePlatform = platform.replace('-sandbox', '')
  const firstLetter = basePlatform.charAt(0).toUpperCase()
  const color = platformColors[platform] || '#1890ff'

  return (
    <Avatar
      style={{
        backgroundColor: color,
        color: '#fff',
        fontSize: '20px',
        fontWeight: 'bold',
      }}
      size={48}
    >
      {firstLetter}
    </Avatar>
  )
}

/**
 * Format date for display
 */
function formatDate(dateString?: string): string {
  if (!dateString) return 'N/A'

  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Connection Card
 *
 * Displays connection information with progressive disclosure UX:
 * - Primary info visible at glance (Debra persona)
 * - Technical details expandable (Sarah persona)
 *
 * @example
 * ```tsx
 * <ConnectionCard
 *   connection={connection}
 *   onAction={(action, conn) => {
 *     if (action === 'pause') {
 *       handlePause(conn.uid)
 *     }
 *   }}
 *   loading={isPending}
 * />
 * ```
 */
export const ConnectionCard: React.FC<ConnectionCardProps> = ({
  connection,
  onAction,
  loading = false,
  style,
  className,
}) => {
  const platformName = getPlatformDisplayName(connection.platform)
  const platformIcon = getPlatformIcon(connection.platform)

  // Get merchant name from cached test result (shows company/business name)
  const merchantName = connection.metadata?.lastTestResult?.accountSummary?.accountName

  return (
    <Card
      style={{
        borderRadius: '8px',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        ...style,
      }}
      className={className}
      bodyStyle={{ padding: '20px' }}
    >
      {/* Header: Logo, Name, Actions */}
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
        {/* Platform Logo */}
        <div style={{ marginRight: '16px' }}>{platformIcon}</div>

        {/* Name and Platform */}
        <div style={{ flex: 1 }}>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {connection.name}
          </Title>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            {platformName}
          </Text>
          {/* Show merchant/company name from cached test result */}
          {merchantName && (
            <div>
              <Text style={{ fontSize: '13px', color: '#595959' }}>
                {merchantName}
              </Text>
            </div>
          )}
        </div>

        {/* Actions Menu */}
        <ConnectionActionsMenu
          connection={connection}
          onAction={onAction}
          disabled={loading}
        />
      </div>

      {/* Status Row: Display State Badge + Health Icon */}
      <div style={{ marginBottom: '12px' }}>
        <Space size="middle">
          <ConnectionStatusBadge connection={connection} />
          <ConnectionHealthIcon
            healthStatus={connection.healthStatus}
            lastTestedAt={connection.lastTestedAt}
          />
        </Space>
      </div>

      {/* Metadata Row */}
      <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '16px' }}>
        {/* External Company ID (account name) */}
        {connection.externalCompanyId && (
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Account:{' '}
            </Text>
            <Text style={{ fontSize: '13px' }}>{connection.externalCompanyId}</Text>
          </div>
        )}

        {/* Last Health Check */}
        {connection.lastTestedAt && (
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Last checked:{' '}
            </Text>
            <Text style={{ fontSize: '13px' }}>{formatDate(connection.lastTestedAt)}</Text>
          </div>
        )}

        {/* Created Date */}
        <div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Connected:{' '}
          </Text>
          <Text style={{ fontSize: '13px' }}>{formatDate(connection.createdAt)}</Text>
        </div>

        {/* Description (if provided) */}
        {connection.description && (
          <div>
            <Text type="secondary" style={{ fontSize: '13px', fontStyle: 'italic' }}>
              {connection.description}
            </Text>
          </div>
        )}
      </Space>

      {/* Technical Details (Expandable) */}
      <ConnectionTechnicalDetails connection={connection} />
    </Card>
  )
}

export default ConnectionCard
